import { useCallback, useEffect } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { AppError } from '@/utils';

import { User } from '../types';

import { useUserQuery } from './use-user-query';

export function useUserInfo(): User;
export function useUserInfo(enabled: true): User;
export function useUserInfo(enabled: false): undefined;
export function useUserInfo(enabled: boolean): User | undefined;
export function useUserInfo(enabled: boolean = true): User | undefined {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: userInfo, isLoading, isSuccess } = useUserQuery({ enabled });

  const handleSubscriptionRedirect = useCallback(() => {
    if (!enabled || !isSuccess || !userInfo || isLoading) return;

    const { is_payment_valid, is_completed, is_subscribed } =
      userInfo.subscription_status;

    if (!is_payment_valid) {
      if (userInfo.user_type === 'standard') {
        if (location.pathname !== '/subscription/error') {
          navigate('/subscription/error', { replace: false });
        }
      } else {
        if (location.pathname !== '/settings/subscriptions') {
          navigate('/settings/subscriptions', { replace: false });
        }
      }
    } else if (!is_completed || !is_subscribed) {
      if (location.pathname !== '/signup/subscription') {
        navigate('/signup/subscription?fromUserInfo=true', { replace: false });
      }
    }
  }, [userInfo, isLoading, isSuccess, enabled, location.pathname, navigate]);

  useEffect(() => {
    handleSubscriptionRedirect();
  }, [handleSubscriptionRedirect]);

  useEffect(() => {
    const handlePopState = () => {
      handleSubscriptionRedirect();
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [handleSubscriptionRedirect]);

  if (!enabled) {
    return undefined;
  }

  if (isLoading) {
    throw AppError.authError();
  }

  if (!userInfo) {
    window.location.replace(ENV.HOME_URL);
    throw new Error('Redirecting to login');
  }

  return userInfo;
}
